import { environment } from '../../environments/environment';
import {
  API_URL_OVERRIDE_REGISTRY,
  ApiUrlOverrideRegistryEntry,
  getRegistryEntry,
  OverridableApiKey,
} from './api-url-registry';

/**
 * Runtime API URL override store (dev / PR only) — framework-agnostic core.
 *
 * Pure module-level state + functions so the `MyHttpClientFactory` chokepoint can
 * resolve overrides WITHOUT a constructor/DI dependency. `ApiUrlOverrideService`
 * is a thin Angular wrapper over this module for the dialog and the menu banner.
 *
 * FAIL-CLOSED: every public function no-ops / returns the original value unless
 * `isOverrideAllowed()` is true (localhost or a PR-numbered azurewebsites host).
 * A hand-set localStorage entry can therefore never leak into a stg/prod origin.
 */

const STORAGE_KEY = 'ups.apiUrlOverrides';

/** Persisted per-API override: the resolved URL + a short label for the banner. */
export interface StoredOverride {
  url: string;
  label: string;
}

export type OverrideMap = Partial<Record<OverridableApiKey, StoredOverride>>;

export interface ActiveOverride {
  apiKey: OverridableApiKey;
  /** Registry display label, e.g. "Sparta API". */
  label: string;
  /** Target label, e.g. "local", "stg", "PR #123", "custom". */
  targetLabel: string;
  url: string;
}

export interface BuiltUrl {
  /** Resolved URL, or null when the target is "default" (i.e. clear the override). */
  url: string | null;
  /** Short label for the banner / storage. */
  label: string;
}

let cachedMap: OverrideMap | null = null;
let initialized = false;
/**
 * Snapshot of the build's ORIGINAL `environment.urls.*` values, captured once
 * before we mutate `environment.urls` in place. The reverse-map and the dialog's
 * "Default" display must read this, not the (possibly already-overridden) live
 * `environment.urls`.
 */
let originalUrls: Partial<Record<OverridableApiKey, string>> | null = null;

function hasWindow(): boolean {
  return typeof window !== 'undefined' && !!window.location;
}

/**
 * FAIL-CLOSED gate. Overrides are honored ONLY when served locally or as a PR
 * deployment. NOT based on `environment.production` — stg builds are also
 * `production:false`, so that would wrongly enable overrides on stg.
 */
export function isOverrideAllowed(): boolean {
  if (!hasWindow()) return false;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    return true;
  }
  // PR app services start with the PR number, e.g. 1315-web-sparta-upsllc.azurewebsites.net.
  // The leading digits correctly exclude stg-*/prod (`*-upsllc` without a numeric prefix).
  return /^\d+-.*-upsllc\.azurewebsites\.net$/i.test(host);
}

/** Parse the PR number from the current host (e.g. `1315-web-sparta-upsllc...` → "1315"). */
export function detectPrNumber(): string | null {
  if (!hasWindow()) return null;
  const match = /^(\d+)-/.exec(window.location.hostname);
  return match ? match[1] : null;
}

function readStorage(): OverrideMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as OverrideMap) : {};
  } catch {
    return {};
  }
}

function writeStorage(map: OverrideMap): void {
  try {
    if (Object.keys(map).length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    }
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

/** Normalized query-param name for an API key, e.g. spartaAPI → "setspartaapi". */
function queryParamName(apiKey: OverridableApiKey): string {
  return `set${apiKey}`.toLowerCase();
}

function captureOriginalUrls(): void {
  if (originalUrls) return;
  const snapshot: Partial<Record<OverridableApiKey, string>> = {};
  for (const entry of API_URL_OVERRIDE_REGISTRY) {
    const current = environment.urls?.[entry.apiKey];
    if (current !== undefined) snapshot[entry.apiKey] = current;
  }
  originalUrls = snapshot;
}

function ensureInitialized(): void {
  if (initialized) return;
  initialized = true;
  // Snapshot the build defaults BEFORE any mutation of environment.urls.
  captureOriginalUrls();
  if (!isOverrideAllowed()) {
    cachedMap = {};
    return;
  }

  const map = readStorage();
  let changed = false;

  // Per-API query params (?setSpartaApi=local&setViewpointApi=pr-123) win over any
  // persisted UI value for their keys, and persist so the override survives the
  // navigation/reload (clear via the dialog Reset or window.__apiUrlOverride.clear).
  const params = new URLSearchParams(window.location.search);
  const lowerParams = new Map<string, string>();
  params.forEach((value, key) => lowerParams.set(key.toLowerCase(), value));
  for (const entry of API_URL_OVERRIDE_REGISTRY) {
    const value = lowerParams.get(queryParamName(entry.apiKey));
    if (value != null) {
      applyTargetToMap(map, entry.apiKey, value);
      changed = true;
    }
  }

  if (changed) writeStorage(map);
  cachedMap = map;
}

/** Apply a raw target string to a map in place (no persistence). */
function applyTargetToMap(
  map: OverrideMap,
  apiKey: OverridableApiKey,
  target: string
): void {
  const entry = getRegistryEntry(apiKey);
  if (!entry) return;
  const built = buildUrl(entry, target);
  if (built.url == null) {
    delete map[apiKey];
  } else {
    map[apiKey] = { url: built.url, label: built.label };
  }
}

/**
 * Resolve a target string to a concrete URL for an API.
 * target ∈ { default | local | stg | prod | pr | pr-<n> | <custom http(s) url> }.
 * Returns `{ url: null }` for "default" (meaning: remove any override).
 */
export function buildUrl(
  entry: ApiUrlOverrideRegistryEntry,
  target: string
): BuiltUrl {
  const t = (target ?? '').trim();
  const lower = t.toLowerCase();

  if (lower === '' || lower === 'default')
    return { url: null, label: 'default' };
  if (lower === 'local') return { url: entry.localUrl ?? null, label: 'local' };
  if (lower === 'stg') return { url: entry.stgUrl ?? null, label: 'stg' };
  if (lower === 'prod') return { url: entry.prodUrl ?? null, label: 'prod' };

  const prMatch = /^pr-?(\d+)?$/i.exec(t);
  if (prMatch) {
    const num = prMatch[1] ?? detectPrNumber();
    if (num && entry.prSlug) {
      return {
        url: `https://${num}-${entry.prSlug}.azurewebsites.net`,
        label: `PR #${num}`,
      };
    }
    return { url: null, label: 'default' };
  }

  if (/^https?:\/\//i.test(t)) return { url: t, label: 'custom' };

  // Unrecognized target → no change.
  return { url: null, label: 'default' };
}

/**
 * Apply active overrides to `environment.urls` IN PLACE so EVERY consumer sees the
 * repointed URL — not only traffic through `MyHttpClientFactory`, but also DIRECT
 * reads of `environment.urls.*` (Syncfusion DataManager `host`, hand-built URIs,
 * raw `fetch`), which never touch the interceptor. Any API without an active
 * override is restored to its build default.
 *
 * Wired to an `APP_INITIALIZER` so it runs before any component reads
 * `environment.urls`, and re-run on every set/clear. No-op when overrides aren't
 * allowed (stg/prod), so a hand-set localStorage can never repoint a real origin.
 */
export function applyApiUrlOverrides(): void {
  ensureInitialized();
  if (!isOverrideAllowed() || !environment.urls || !originalUrls) return;
  const map = cachedMap ?? {};
  for (const entry of API_URL_OVERRIDE_REGISTRY) {
    const original = originalUrls[entry.apiKey];
    if (original === undefined) continue;
    environment.urls[entry.apiKey] = map[entry.apiKey]?.url ?? original;
  }
}

/**
 * The HTTP chokepoint hook. Given the base URL a service passed to
 * `createHttpClient`, return the overridden URL if one is active for that API,
 * else the unchanged base URL. Reverse-maps against the ORIGINAL build defaults
 * (not the live, possibly-mutated `environment.urls`) so it stays correct
 * regardless of whether `applyApiUrlOverrides()` has already swapped the value.
 */
export function resolveApiUrlOverride(baseUrl: string): string {
  ensureInitialized();
  if (!baseUrl || !cachedMap || !isOverrideAllowed()) return baseUrl;

  const origins = originalUrls ?? {};
  for (const entry of API_URL_OVERRIDE_REGISTRY) {
    const original = origins[entry.apiKey];
    if (original && original === baseUrl) {
      const override = cachedMap[entry.apiKey];
      return override ? override.url : baseUrl;
    }
  }
  return baseUrl;
}

/** Current override map (copy). Empty when overrides aren't allowed. */
export function getOverrideMap(): OverrideMap {
  ensureInitialized();
  return { ...(cachedMap ?? {}) };
}

/** Overrides currently active, for the dialog and the menu banner. */
export function getActiveOverrides(): ActiveOverride[] {
  ensureInitialized();
  const map = cachedMap ?? {};
  return (Object.keys(map) as OverridableApiKey[]).map((apiKey) => {
    const entry = getRegistryEntry(apiKey);
    const stored = map[apiKey] as StoredOverride;
    return {
      apiKey,
      label: entry?.label ?? apiKey,
      targetLabel: stored.label,
      url: stored.url,
    };
  });
}

/** Set (or, for `default`, clear) the override for one API and persist it. */
export function setTarget(apiKey: OverridableApiKey, target: string): void {
  ensureInitialized();
  if (!isOverrideAllowed()) return;
  const map = { ...(cachedMap ?? {}) };
  applyTargetToMap(map, apiKey, target);
  cachedMap = map;
  writeStorage(map);
  applyApiUrlOverrides();
}

/** Clear all overrides and restore the build defaults on `environment.urls`. */
export function clearAllOverrides(): void {
  ensureInitialized();
  cachedMap = {};
  writeStorage({});
  applyApiUrlOverrides();
}

/**
 * Current build DEFAULT URL for an API (the value before any override), for the
 * dialog's "Default" option — reads the original snapshot, not the live
 * (possibly mutated) `environment.urls`.
 */
export function getCurrentDefaultUrl(
  apiKey: OverridableApiKey
): string | undefined {
  ensureInitialized();
  return originalUrls?.[apiKey] ?? environment.urls?.[apiKey];
}

/** Test-only: reset module state so a spec can re-capture defaults from a stub. */
export function resetApiUrlOverrideStateForTests(): void {
  initialized = false;
  cachedMap = null;
  originalUrls = null;
}
